/**
 * Conversation service for managing chat conversations via backend API
 */

import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

export interface ConversationMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  sources?: any[];
  confidence?: number;
  suggested_actions?: any[];
  requires_escalation?: boolean;
  metadata?: any;
}

export interface Conversation {
  id: string;
  tenant_id: string;
  user_id: string;
  title: string;
  messages: ConversationMessage[];
  created_at: string;
  updated_at: string;
  metadata?: any;
}

export interface ConversationCreate {
  title: string;
  metadata?: any;
}

export interface ConversationUpdate {
  title?: string;
  metadata?: any;
}

export interface MessageCreate {
  content: string;
  role: 'user' | 'assistant' | 'system';
  sources?: any[];
  confidence?: number;
  suggested_actions?: any[];
  requires_escalation?: boolean;
  metadata?: any;
}

class ConversationService {
  private apiClient: AxiosInstance;

  constructor() {
    // Use runtime environment detection to switch between dev and prod APIs
    const { getConfig } = require('../config/environment');
    const config = getConfig();
    const API_BASE_URL = config.apiUrl;
    
    this.apiClient = axios.create({
      baseURL: `${API_BASE_URL}/api/v1/conversations`,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth interceptor
    this.apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  async createConversation(conversationData: ConversationCreate): Promise<Conversation> {
    try {
      const response = await this.apiClient.post('/', conversationData);
      return response.data;
    } catch (error: any) {
      console.error('Conversation creation error:', error);
      console.error('Error response:', error.response?.data);
      throw new Error(error.response?.data?.detail || 'Failed to create conversation');
    }
  }

  async listConversations(limit: number = 50): Promise<Conversation[]> {
    try {
      const response = await this.apiClient.get(`/?limit=${limit}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to list conversations');
    }
  }

  async getConversation(conversationId: string): Promise<Conversation> {
    try {
      const response = await this.apiClient.get(`/${conversationId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get conversation');
    }
  }

  async updateConversation(conversationId: string, updateData: ConversationUpdate): Promise<Conversation> {
    try {
      const response = await this.apiClient.put(`/${conversationId}`, updateData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to update conversation');
    }
  }

  async deleteConversation(conversationId: string): Promise<void> {
    try {
      await this.apiClient.delete(`/${conversationId}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to delete conversation');
    }
  }

  async addMessage(conversationId: string, messageData: MessageCreate): Promise<Conversation> {
    try {
      const response = await this.apiClient.post(`/${conversationId}/messages`, messageData);
      return response.data;
    } catch (error: any) {
      console.error('Add message error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to add message');
    }
  }

  async searchConversations(query: string, limit: number = 20): Promise<Conversation[]> {
    try {
      const response = await this.apiClient.get(`/search?q=${encodeURIComponent(query)}&limit=${limit}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to search conversations');
    }
  }
}

export const conversationService = new ConversationService();
